import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Upload, User, Mail, Phone, GraduationCap, Users, MailCheck, PhoneCall } from 'lucide-react';
import { ImportedStudent } from './types';

interface StudentPreviewCardProps {
  students: ImportedStudent[];
}

export const StudentPreviewCard = ({ students }: StudentPreviewCardProps) => {
  return (
    <Card className="w-full">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-3 text-xl">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Upload className="w-6 h-6 text-primary" />
          </div>
          Vista previa de alumnos a importar
        </CardTitle>
        <CardDescription className="text-base">
          Se encontraron <span className="font-semibold text-primary">{students.length} alumnos</span> en el archivo
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {/* Responsive table wrapper */}
        <div className="w-full overflow-x-auto rounded-lg border bg-background">
          <Table className="min-w-[600px] w-full">
            <TableHeader className="sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 shadow">
              <TableRow className="border-b-2">
                <TableHead className="py-4 text-base font-semibold w-1/4 min-w-[160px]">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Nombre
                  </div>
                </TableHead>
                <TableHead className="py-4 text-base font-semibold w-1/6 min-w-[100px]">
                  <div className="flex items-center gap-2">
                    <GraduationCap className="w-4 h-4" />
                    Clase
                  </div>
                </TableHead>
                <TableHead className="py-4 text-base font-semibold w-1/4 min-w-[180px]">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email
                  </div>
                </TableHead>
                <TableHead className="py-4 text-base font-semibold w-1/4 min-w-[150px]">
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Teléfono
                  </div>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {students.map((s, i) => (
                <TableRow key={i} className="hover:bg-accent/40 even:bg-muted/30 transition-colors border-b last:border-b-0">
                  <TableCell className="py-3 px-2 align-middle max-w-[180px] truncate">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="w-4 h-4 text-primary" />
                      </div>
                      <span className="font-medium text-base truncate max-w-[120px]">{s.nombre}</span>
                    </div>
                  </TableCell>
                  <TableCell className="py-3 px-2 align-middle max-w-[100px] truncate">
                    <Badge variant="outline" className="text-sm px-3 py-1">
                      {s.clase}
                    </Badge>
                  </TableCell>
                  <TableCell className="py-3 px-2 align-middle max-w-[180px] truncate">
                    <div className="flex items-center gap-2">
                      {s.email ? (
                        <>
                          <MailCheck className="w-4 h-4 text-success" />
                          <span className="text-sm truncate max-w-[120px]">{s.email}</span>
                        </>
                      ) : (
                        <span className="text-muted-foreground text-sm">No proporcionado</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="py-3 px-2 align-middle max-w-[150px] truncate">
                    <div className="flex items-center gap-2">
                      {s.telefono ? (
                        <>
                          <PhoneCall className="w-4 h-4 text-primary" />
                          <span className="text-sm truncate max-w-[90px]">{s.telefono}</span>
                        </>
                      ) : (
                        <span className="text-muted-foreground text-sm">No proporcionado</span>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {/* Resumen estadístico */}
        <div className="border-t bg-muted/30 p-4 mt-4 rounded-b-lg flex flex-col md:flex-row md:items-center md:justify-between gap-3 w-full">
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <Users className="w-4 h-4 text-primary" />
            Total de alumnos: <span className="font-semibold text-foreground">{students.length}</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <MailCheck className="w-4 h-4 text-success" />
            Con email: <span className="font-semibold text-foreground">{students.filter(s => s.email).length}</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <PhoneCall className="w-4 h-4 text-primary" />
            Con teléfono: <span className="font-semibold text-foreground">{students.filter(s => s.telefono).length}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};